import { useMemo } from 'react'

import { faUser, faUserSlash } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'
import {
  Box,
  Checkbox,
  DashboardModule,
  Link,
  StatusBadge,
  Table,
  Text,
  UserProfileImage,
} from '@domain-group/suit-theme'

import type { useTaskListItemsPayload } from '@hooks/useTaskListItems'
import type { TaskListItemModel } from '@models/taskListItem'
import type { EntitiesState } from '@redux/entities'

import { getRelatedEntityOwnerOrSubject } from '@functions/relatedEntity'
import { getColor } from '@functions/status'

import type { PriorityKey } from '@functions/status'

import { generateUrls } from '@hooks/useTaskList'
import { generateUrls as generateTaskListItemUrls } from '@hooks/useTaskListItem'
import { UseToggleListItemPayload } from '@hooks/useToggleListItem'

import DateBadge from '@components/DateBadge'

const watchEntityKeys = ['statuses', 'taskListItems']

const buildColumns = (
  entities: EntitiesState,
  selectedTaskListItemIds: number[],
  toggleItem: UseToggleListItemPayload['callbacks']['toggleItem'],
) => {
  const {
    brands, images, statuses, taskLists, users,
  } = entities

  return [
    {
      title: '',
      dataKey: 'id',
      cellProps: {
        verticalAlign: 'middle',
        width: 18,
      },
      render: (id: number) => (
        <Checkbox
          boxProps={{ width: 18 }}
          checked={selectedTaskListItemIds.includes(id)}
          onClick={() => toggleItem(id)}
          marginRight="0"
        />
      ),
    },
    {
      title: 'Title',
      dataKey: 'title',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (title: string, item: TaskListItemModel) => {
        const { editTaskListItemUrl } = generateTaskListItemUrls(item, { includeRedirect: true })

        return (
          <Link
            href={editTaskListItemUrl}
            textProps={{
              color: 'bodyFontColor',
              display: 'inline-block',
              variant: 'ellipsis',
              width: 320,
            }}
          >
            {title}
          </Link>
        )
      },
    },
    {
      title: 'Brand',
      dataKey: 'brand_id',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (brand_id: number) => {
        const brand = brands[brand_id] || {}

        return (
          <Text
            color="bodyFontColor"
            display="inline-block"
            variant="ellipsis"
            width={320}
          >
            {brand.title || 'N/A'}
          </Text>
        )
      },
    },
    {
      title: 'Project Title',
      dataKey: 'task_list_id',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (task_list_id: number) => {
        const taskList = taskLists[task_list_id] || {}
        const { editTaskListUrl } = generateUrls(taskList)

        return (
          <Link
            href={editTaskListUrl}
            textProps={{
              color: 'bodyFontColor',
              display: 'inline-block',
              variant: 'ellipsis',
              width: 240,
            }}
          >
            {taskList?.title}
          </Link>
        )
      },
    },
    {
      title: 'Template #',
      dataKey: 'template_page_id',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      // @ts-expect-error TS7006
      render: (_, item: TaskListItemModel) => {
        const templatePage = getRelatedEntityOwnerOrSubject({
          entities,
          filters: { 'data.template_type': 'new' },
          // @ts-expect-error TS2322
          item,
          returnType: 'subject',
          subjectType: 'TemplatePage',
        })

        return (
          <Link
            textProps={{
              color: templatePage?.id ? 'bodyFontColor' : 'lightGrey',
              fontStyle: templatePage?.id ? undefined : 'italic',
            }}
          >
            {templatePage?.id || 'Not Set'}
          </Link>
        )
      },
    },
    {
      title: 'Due Date',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'dates.due.date',
      render: (dueDate: string) => {
        if (!dueDate) return (<Text fontStyle="italic">Not Set</Text>)

        return (
          <DateBadge date={dueDate} />
        )
      },
    },
    {
      title: 'Priority',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'priority',
      render: (priority: PriorityKey) => {
        const priorityKey = priority || 'default'
        // @ts-expect-error TS2322
        return <StatusBadge color={getColor('priority', priorityKey)} ghost text={priorityKey} />
      },
    },
    {
      title: 'Assignee',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 91,
      },
      dataKey: 'assignee_id',
      render: (assigneeId: number) => {
        const assignee = users[assigneeId] || {}
        // @ts-expect-error TS2538
        const image = images[assignee.image] || {}

        return (
          <UserProfileImage
            boxProps={{ display: 'inline-flex' }}
            hintComponent={(
              <UserProfileImage.Hint>
                {assignee.full_name || 'Unassigned'}
              </UserProfileImage.Hint>
            )}
            imageUrl={image.thumb_url}
          >
            <FontAwesomeIcon icon={assignee.id ? faUser : faUserSlash} />
          </UserProfileImage>
        )
      },
    },
    {
      title: 'Status',
      dataKey: 'status_id',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      render: (statusId: number) => {
        const status = statuses[statusId] || {}

        return <StatusBadge color={getColor('status', status.key)} ghost text={status.title || 'Pending'} />
      },
    },
  ]
}

interface TasksTableProps {
  entities: EntitiesState,
  taskListItemsPayload: useTaskListItemsPayload,
  useToggleListItemPayload: UseToggleListItemPayload,
}

const TasksTable = (props: TasksTableProps) => {
  const {
    entities,
    taskListItemsPayload: {
      callbacks: {
        loadMore,
      },
      canLoadMore,
      filteredTaskListItems,
      filteredTaskListItemsCount,
      loading,
    },
    useToggleListItemPayload,
  } = props

  const {
    updatedEntities: {
      statuses: statusesUpdatedAt,
      taskListItems: taskListItemsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { items: selectedTaskListItemIds, callbacks: { toggleItem } } = useToggleListItemPayload

  const memoColumns = useMemo(
    () => buildColumns(entities, selectedTaskListItemIds, toggleItem),
    [filteredTaskListItemsCount, statusesUpdatedAt, taskListItemsUpdatedAt, selectedTaskListItemIds.length],
  )

  return (
    <DashboardModule
      loading={loading}
      title="Tasks"
    >
      <Box flexDirection="column">
        <Table
          boxProps={{ border: 'none', overflowY: 'auto' }}
          // @ts-expect-error TS2322
          columns={memoColumns}
          // @ts-expect-error TS2322
          data={filteredTaskListItems}
          stickyHeader
        />

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredTaskListItemsCount}
          loading={loading}
        />
      </Box>
    </DashboardModule>
  )
}

export default TasksTable
